import { unwrap } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Badge, Button, Icon, cn, toast } from "@sagot/ui";
import { useMutation } from "@tanstack/react-query";
import { useErrorMessage } from "../../../lib/api-errors";
import { useApi } from "../../../session/session-provider";
import { useInvalidateSource, useReplaceCachedChunk } from "./use-source-detail";
import type { ChunkView } from "../shared/types";

/** Ordinals are 0-based; the UI numbers chunks from #1 like the mockup. */
export function chunkNumber(chunk: Pick<ChunkView, "ordinal">): number {
  return chunk.ordinal + 1;
}

interface ChunkCardProps {
  chunk: ChunkView;
  sourceId: string;
  canEdit: boolean;
  playgroundHref: string | null;
  onEdit: (chunk: ChunkView) => void;
}

function PageBadge({ chunk }: { chunk: ChunkView }) {
  const { t } = useTranslation();
  if (!chunk.page_start) return null;
  const k = "deployKnowledge.sourceDetail.chunks";
  const multi = chunk.page_end !== null && chunk.page_end !== chunk.page_start;
  return <Badge>{multi ? t(`${k}.pages`, { from: chunk.page_start, to: chunk.page_end }) : t(`${k}.page`, { page: chunk.page_start })}</Badge>;
}

/** One chunk: #n, page, heading path, tokens, context, text, Edit / Exclude|Include / Test. */
export function ChunkCard({ chunk, sourceId, canEdit, playgroundHref, onEdit }: ChunkCardProps) {
  const { t } = useTranslation();
  const api = useApi();
  const message = useErrorMessage();
  const replace = useReplaceCachedChunk(sourceId);
  const invalidate = useInvalidateSource(sourceId);
  const k = "deployKnowledge.sourceDetail.chunks";
  const n = chunkNumber(chunk);
  const toggle = useMutation({
    mutationFn: (excluded: boolean) => unwrap(api.PATCH("/v1/chunks/{chunk_id}", { params: { path: { chunk_id: chunk.id } }, body: { excluded } })),
    onSuccess: async (updated) => {
      replace(updated);
      if (updated.is_excluded) toast.warning(t(`${k}.excludedToast`, { n }));
      else toast.success(t(`${k}.includedToast`, { n }));
      await invalidate();
    },
    onError: (error) => toast.error(message(error)),
  });
  const excluded = chunk.is_excluded;
  return (
    <article className={cn("rounded-lg border border-line p-4", excluded ? "bg-surface-2" : "bg-surface")} aria-label={`#${String(n)}`} data-testid="chunk-card">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-xs font-bold text-fg">#{n}</span>
        <PageBadge chunk={chunk} />
        <span className="min-w-0 truncate text-xs font-semibold text-fg-muted">{chunk.heading_path.join(" › ")}</span>
        <span className="ml-auto flex items-center gap-2">
          <span className="font-mono text-[11px] text-fg-subtle">{t(`${k}.tokens`, { count: chunk.token_count })}</span>
          {chunk.manually_edited ? <Badge tone="brand">{t(`${k}.edited`)}</Badge> : null}
          {excluded ? (
            <Badge tone="warning">
              <Icon name="eye-off" className="size-3" />
              {t(`${k}.excludedBadge`)}
            </Badge>
          ) : null}
        </span>
      </div>
      {chunk.context ? (
        <div className="mt-3 rounded-md border-l-2 border-accent bg-accent-soft px-3 py-2">
          <div className="text-[10px] font-bold uppercase tracking-wide text-accent-ink">{t(`${k}.contextLabel`)}</div>
          <p className="mt-0.5 text-xs leading-relaxed text-fg">{chunk.context}</p>
        </div>
      ) : null}
      <p className={cn("mt-3 whitespace-pre-line text-sm leading-relaxed", excluded ? "text-fg-muted" : "text-fg")}>{chunk.content}</p>
      <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-line pt-3">
        {canEdit ? (
          <>
            <Button variant="secondary" size="sm" aria-label={t(`${k}.editLabel`, { n })} onClick={() => { onEdit(chunk); }}>
              <Icon name="pencil" />
              {t(`${k}.edit`)}
            </Button>
            <Button variant="ghost" size="sm" aria-label={t(excluded ? `${k}.includeLabel` : `${k}.excludeLabel`, { n })} loading={toggle.isPending} onClick={() => { toggle.mutate(!excluded); }}>
              <Icon name={excluded ? "eye" : "eye-off"} />
              {t(excluded ? `${k}.include` : `${k}.exclude`)}
            </Button>
          </>
        ) : null}
        {playgroundHref ? (
          <Button asChild variant="ghost" size="sm" className="ml-auto">
            <AppLink href={playgroundHref}>
              <Icon name="messages-square" />
              {t(`${k}.test`)}
            </AppLink>
          </Button>
        ) : null}
      </div>
    </article>
  );
}
